import Avatar from '@/common/components/atom/avatar';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useProfileBarImage } from '@/modules/profile/store/profileBarImage';
import { isDefaultDoctorProfileImage } from '@/modules/profile/utils/doctorProfileImageUrl';
import AppBar from './appBar';

/**
 * New Android app (utils/newAppShell): the site Header is never shown; layouts render this shared
 * AppBar instead, following common app patterns (Digikala, Divar, Telegram):
 * - bottom-nav tab roots: no bar — the page's own search/heading is the top of the screen;
 * - profile/detail pages: back + actions; the page's own heading (doctor name…) slides into the bar
 *   once it scrolls away (Telegram/Snapp profile pattern);
 * - every other page: back + short title.
 * A page that renders its own AppBar hides this one (CSS :has in globals.css), so never two bars.
 */
const TAB_ROOTS = new Set([
  '/',
  '/apphome',
  '/s/[[...params]]',
  '/_',
  '/_/chats/[[...hami]]',
  '/_/profile',
  '/patient',
  '/patient/appointments',
  '/dashboard/appointments',
  '/dashboard/apps/drapp/appointments',
]);

export const isNewAppTabRoot = (pathname: string) => TAB_ROOTS.has(pathname);

/** Pages whose content opens with their own name/heading: the bar shows back only, never a repeat. */
const DETAIL_PAGES = new Set(['/dr/[slug]', '/center/[slug]', '/faq', '/about-us', '/contact-us', '/privacy-policy']);

/** Short in-app titles; anything not listed falls back to the page's <title> without the brand. */
const ROUTE_TITLES: Record<string, string> = {
  '/booking/[slug]': 'دریافت نوبت',
  '/booking/finalize': 'دریافت نوبت',
  '/factor/[centerId]/[bookId]': 'پرداخت',
  '/factor/v2/[slug]/[service_id]': 'پرداخت',
  '/factor/v3': 'پرداخت',
  '/receipt/[centerId]/[bookId]': 'رسید نوبت',
  '/invoice/[centerId]/[bookId]': 'صورت‌حساب',
  '/patient/payment/callback/[id]': 'نتیجه پرداخت',
};

const BRAND_SUFFIX = /\s*\|\s*[^|]*$/;

/** The page's own <title> (set by Seo after mount), kept current across client navigations. */
const useDocumentTitle = () => {
  const [title, setTitle] = useState('');
  useEffect(() => {
    const read = () => setTitle(document.title.replace(BRAND_SUFFIX, '').trim());
    read();
    const observer = new MutationObserver(read);
    observer.observe(document.head, { subtree: true, childList: true, characterData: true });
    return () => observer.disconnect();
  }, []);
  return title;
};

/** Detail pages: the page's first <h1> text, and whether it has scrolled up under the bar. */
const useCollapsedHeading = (enabled: boolean) => {
  const { asPath } = useRouter();
  const [state, setState] = useState({ text: '', hidden: false });
  useEffect(() => {
    if (!enabled) return;
    setState({ text: '', hidden: false });
    let observer: IntersectionObserver | undefined;
    let frame = 0;
    let tries = 0;
    // The heading may render after this effect (client-fetched profile shell): look for it a while.
    const attach = () => {
      // The visible page's heading: kept-alive pages (search) stay in the DOM, hidden, with their own
      // headings (a result card repeats the doctor's name) and must never be picked.
      const heading = Array.from(document.querySelectorAll<HTMLElement>('[data-collapse-anchor], h1')).find(
        el => el.getClientRects().length > 0,
      );
      if (!heading) {
        if (tries++ < 300) frame = requestAnimationFrame(attach);
        return;
      }
      observer = new IntersectionObserver(
        ([entry]) => {
          // A heading React replaced (skeleton -> real profile) reports "not intersecting" from the DOM it left:
          // that is no scroll. Drop it and look for the live one.
          if (!heading.isConnected) {
            observer?.disconnect();
            tries = 0;
            setState({ text: '', hidden: false });
            frame = requestAnimationFrame(attach);
            return;
          }
          setState({ text: heading.textContent?.trim() ?? '', hidden: !entry.isIntersecting && entry.boundingClientRect.top < 56 });
        },
        { rootMargin: '-56px 0px 0px 0px' },
      );
      observer.observe(heading);
    };
    attach();
    return () => {
      cancelAnimationFrame(frame);
      observer?.disconnect();
    };
  }, [enabled, asPath]);
  return state;
};

export interface NewAppHeaderBarProps {
  title?: string;
  backButton?: boolean;
  /** Detail pages: small round image shown next to the collapsing title (doctor photo). */
  image?: string;
}

export const NewAppHeaderBar = ({ title, backButton, image: layoutImage }: NewAppHeaderBarProps) => {
  const { pathname } = useRouter();
  // The profile page's live photo beats the layout's (server-prop) one, which is the placeholder on client navigations.
  const pageImage = useProfileBarImage(state => state.image);
  const image = pageImage ?? (isDefaultDoctorProfileImage(layoutImage) ? undefined : layoutImage);
  const documentTitle = useDocumentTitle();
  const isTabRoot = isNewAppTabRoot(pathname);
  const isDetail = DETAIL_PAGES.has(pathname);
  const heading = useCollapsedHeading(isDetail && !isTabRoot);
  // A photo that fails to load falls back to the initials avatar, never a broken-image icon.
  const [imageFailed, setImageFailed] = useState(false);
  useEffect(() => setImageFailed(false), [image]);
  // Lets the page's own bar extras (profile view count) step aside while the title is in.
  const collapsed = isDetail && heading.hidden;
  useEffect(() => {
    document.documentElement.toggleAttribute('data-app-bar-collapsed', collapsed);
    return () => document.documentElement.removeAttribute('data-app-bar-collapsed');
  }, [collapsed]);
  if (isTabRoot) return null;

  if (isDetail) {
    const name = title || heading.text;
    return (
      <AppBar
        data-layout-app-bar=""
        title={name}
        titleRevealed={heading.hidden && !!name}
        titleLeading={
          image ? (
            <Avatar
              src={imageFailed ? undefined : image}
              alt=""
              name={name}
              width={28}
              height={28}
              className="shrink-0 text-[10px]"
              onError={() => setImageFailed(true)}
            />
          ) : undefined
        }
        backButton={backButton ?? true}
        // Profile: out of the flow. The profile reserves the bar's 56px itself (globals.css), so the content
        // never moves when the bar mounts a beat after it (client navigation from search).
        className={pathname === '/dr/[slug]' ? 'print:hidden !fixed inset-x-0 top-0' : 'print:hidden'}
      />
    );
  }

  const resolvedTitle = title || ROUTE_TITLES[pathname] || documentTitle;
  return (
    <AppBar
      data-layout-app-bar=""
      title={resolvedTitle}
      titleLoading={!resolvedTitle}
      backButton={backButton ?? true}
      className="print:hidden"
    />
  );
};

export default NewAppHeaderBar;
